/**
 * alignedEdgeClient — INIT-2026-Q3-SCENE-HIERARCHY close-out C4.
 *
 * Persists an `alignedTo` provenance edge to the platform so a CRS alignment
 * decision survives reload (G4). Best-effort: it fetches the current gTwin
 * version and POSTs the edge to
 *   POST /api/v1/gtwins/{id}/scene/aligned-edges
 * It NEVER throws — the in-session `recordAlignedToEdge` audit log remains the
 * source of truth for the UI; this just makes the decision durable server-side.
 */

import type { AlignedToEdgeRecord } from "@/state/sceneHierarchyStore";

export interface PersistAlignedEdgeOptions {
  /** Base URL for the gTwin API (defaults to the same proxy the SDK uses). */
  baseUrl?: string;
  /** Tenant id header (defaults to "dev-tenant"). */
  tenant?: string;
  /** Injectable fetch for testing. */
  fetchImpl?: typeof fetch;
}

export interface PersistAlignedEdgeResult {
  ok: boolean;
  version?: number;
  error?: string;
}

function resolveBaseUrl(explicit?: string): string {
  if (explicit && explicit.trim()) return explicit.replace(/\/?$/, "/");
  const fromEnv =
    typeof import.meta !== "undefined" ? import.meta.env?.VITE_GTWIN_API_URL : undefined;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).replace(/\/?$/, "/");
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/gtwin-api/`;
  }
  return "http://127.0.0.1:5173/gtwin-api/";
}

function devWarn(message: string, err?: unknown): void {
  if (import.meta.env?.DEV) {
    console.warn(`[alignedEdge] ${message}`, err ?? "");
  }
}

/**
 * Persist an alignedTo edge for `gtwinId`. Resolves the current version then
 * POSTs the edge. Returns `{ ok }` — never throws.
 */
export async function persistAlignedToEdge(
  gtwinId: string,
  edge: AlignedToEdgeRecord,
  opts: PersistAlignedEdgeOptions = {},
): Promise<PersistAlignedEdgeResult> {
  const base = resolveBaseUrl(opts.baseUrl);
  const tenant = opts.tenant ?? "dev-tenant";
  const fetchImpl = opts.fetchImpl ?? fetch;
  const headers = { "content-type": "application/json", "x-tenant-id": tenant };
  const id = encodeURIComponent(gtwinId);

  try {
    const snap = await fetchImpl(`${base}api/v1/gtwins/${id}`, { method: "GET", headers });
    if (!snap.ok) {
      devWarn(`version fetch failed (HTTP ${snap.status})`);
      return { ok: false, error: `version-fetch-${snap.status}` };
    }
    const row = (await snap.json()) as { version?: number };
    const parentVersion = typeof row.version === "number" ? row.version : 0;

    const res = await fetchImpl(`${base}api/v1/gtwins/${id}/scene/aligned-edges`, {
      method: "POST",
      headers,
      body: JSON.stringify({ parentVersion, by: edge.decisionBy, edge }),
    });
    if (!res.ok) {
      devWarn(`edge persist failed (HTTP ${res.status})`);
      return { ok: false, error: `persist-${res.status}` };
    }
    const out = (await res.json()) as { version?: number };
    return { ok: true, version: out.version };
  } catch (err) {
    devWarn("edge persist threw", err);
    return { ok: false, error: "exception" };
  }
}
